"use client"

import type React from "react"
import { useState } from "react"
import { MapPin, Phone, Mail, Clock } from "lucide-react"

const ContactPage: React.FC = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    message: "",
  })

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  return (
    <div>
      {/* Hero Section */}
      <section className="relative bg-red-600 text-white">
        <div className="absolute inset-0 bg-black opacity-30"></div>
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('/images/hero/hero1.png')`,
            backgroundBlendMode: "overlay",
          }}
        ></div>
        <div className="container mx-auto px-4 py-12 relative z-10">
          <div className="min-h-[120px]" aria-hidden="true"></div>
        </div>
      </section>

      {/* Contact Information and Form */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row gap-12">
            {/* Contact Information */}
            <div className="lg:w-1/3">
              <h2 className="text-2xl font-bold mb-6 relative inline-block">
                Contact Details
                <span className="absolute bottom-0 left-0 w-16 h-1 bg-red-600" style={{ marginBottom: "-8px" }}></span>
              </h2>

              <div className="bg-gray-50 p-6 rounded-lg shadow-md">
                <div className="mb-6">
                  <div className="flex items-start mb-3">
                    <MapPin className="text-red-600 mr-3 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold">Canadian Inquiries</h3>
                      <p className="text-gray-600">
                        Petroland Equipment Solutions Ltd.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center mb-3">
                    <Phone className="text-red-600 mr-3 flex-shrink-0" />
                    <div>
                      <h3 className="font-semibold">Phone</h3>
                      <a href="tel:+14037087336" className="text-gray-600 hover:text-red-600">
                        +1 403-708-7336
                      </a>
                    </div>
                  </div>

                  <div className="flex items-center mb-3">
                    <Mail className="text-red-600 mr-3 flex-shrink-0" />
                    <div>
                      <h3 className="font-semibold">Email</h3>
                      <a href="mailto:sales@petrolandesl.com" className="text-gray-600 hover:text-red-600">
                        sales@petrolandesl.com
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <Clock className="text-red-600 mr-3 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold">Business Hours</h3>
                      <p className="text-gray-600">
                        Monday - Friday: 8:00 AM - 5:00 PM
                        <br />
                        Saturday - Sunday: Closed
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:w-2/3">
              <h2 className="text-2xl font-bold mb-6 relative inline-block">
                Send an Inquiry
                <span className="absolute bottom-0 left-0 w-16 h-1 bg-red-600" style={{ marginBottom: "-8px" }}></span>
              </h2>

              <form action="mailto:sales@petrolandesl.com" method="post" encType="text/plain" className="bg-gray-50 p-6 rounded-lg shadow-md">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label htmlFor="name" className="block text-gray-700 font-medium mb-2">
                      Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-600 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-gray-700 font-medium mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-600 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-gray-700 font-medium mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-600 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label htmlFor="company" className="block text-gray-700 font-medium mb-2">
                      Company Name
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-600 focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="mb-6">
                  <label htmlFor="message" className="block text-gray-700 font-medium mb-2">
                      Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-600 focus:border-transparent"
                  ></textarea>
                </div>

                <button
                  type="submit"
                  className="bg-red-600 hover:bg-red-700 text-white font-medium py-3 px-6 rounded-md transition-colors"
                >
                  Request a Quote
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default ContactPage
